import React, { useEffect, useState, useRef } from 'react';
import {
  View, Text, ActivityIndicator, TouchableOpacity, ScrollView,
  Image, Linking, StyleSheet, Modal, Pressable, SafeAreaView,
} from 'react-native';
import { get, post, url as apiUrl, mediaUrl } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { BLUE, BLUE_DARK, PINK, PINK_DARK, BG, CARD, TEXT, TEXT_SUB, TEXT_MUTED, DIVIDER, BLUE_LIGHT } from '../../theme/colors';

const JUMP_LABELS: Record<string, string> = {
  STREKKHOPP:             'Strekkhopp',
  KROPPERT_HOPP:          'Kroppert hopp',
  PIKERT_HOPP:            'Pikert hopp',
  KROPPERT_SALTO:         'Kroppert salto',
  PIKERT_SALTO:           'Pikert salto',
  STRAK_SALTO:            'Strak salto',
  SALTO_HALV_SKRU:        'Salto med halv skru',
  SALTO_HEL_SKRU:         'Salto med hel skru',
  SALTO_HALVANNEN_SKRU:   'Salto med halvannen skru',
  DOBBEL_SALTO:           'Dobbel salto',
  DOBBEL_SALTO_HALV_SKRU: 'Dobbel salto med halv skru',
};

const JUMP_DESC: Record<string, string> = {
  STREKKHOPP:
    'Et strakt hopp med armene rett opp inntil ørene.',
  PIKERT_HOPP:
    'Gymnasten folder seg i lufta med strake bein frem foran kroppen. I denne konkurransen teller det som pikert hopp både med samlede bein og med bensplitt.',
  KROPPERT_HOPP:
    'Knærne trekkes raskt opp mot brystet før kroppen åpnes igjen, lett å se om formen er skarp.',
  STRAK_SALTO:
    'En salto i rett linje: kroppen skal være strak og spent hele veien rundt. Er den saltoen med saktest rotasjon.',
  PIKERT_SALTO:
    'Salto der kroppen knekkes i hofta med strake bein — først høyde, så en tydelig fold, før utstrekning mot landing.',
  KROPPERT_SALTO:
    'I kroppert salto trekker gymnasten bena sammen mot brystet, roterer raskt, og åpner presist før landingen.',
  SALTO_HALV_SKRU:
    'Salto med en halv skru: turneren skal først opp og rundt, så vende kroppen kontrollert før landing baklengs.',
  SALTO_HEL_SKRU:
    'Salto med hel skru: høyde, salto og en hel rotasjon rundt egen akse — skruen må være ferdig før landingen.',
  SALTO_HALVANNEN_SKRU:
    'En krevende salto med halvannen skru: det skal se ut som turneren venter i lufta, starter skruen rent og rekker å gjøre seg klar til landing.',
  DOBBEL_SALTO:
    'Dobbel salto i samme hopp: her handler alt om høyde, fart og timing — to hele rotasjoner før en kontrollert landing.',
  DOBBEL_SALTO_HALV_SKRU:
    'Dobbel salto med skru: først nok høyde til to rotasjoner, så en presis vending i lufta uten å miste form eller landing.',
};

const CLASS_LABELS: Record<string, string> = {
  CLASS_1: 'Klasse 1', CLASS_2: 'Klasse 2', CLASS_3: 'Klasse 3',
  CLASS_4: 'Klasse 4', CLASS_5: 'Klasse 5',
};

export default function CurrentRunScreen({ route }: any) {
  const competitionId: number = route.params.competitionId;
  const competitionName: string = route.params?.competitionName ?? '';
  const competitionStatus: string | undefined = route.params?.competitionStatus;
  const [data, setData] = useState<any | null>(null);
  const [scores, setScores] = useState<any | null>(null);
  const [deal, setDeal] = useState<any | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [dealOpen, setDealOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [heartsSent, setHeartsSent] = useState(0);
  const session = useSession();
  const sinceRef = useRef<number | null>(null);
  const runIdRef = useRef<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scoresPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCurrent = async () => {
    try {
      const path = sinceRef.current != null
        ? `/competitions/${competitionId}/current-run?since=${sinceRef.current}`
        : `/competitions/${competitionId}/current-run`;
      const res = await get<any>(path);
      if (res.unchanged) return;
      setData(res);
      if (typeof res.state_version === 'number') {
        sinceRef.current = res.state_version;
      }
      // Reset hearts counter when run changes
      const newRunId = res.current_run?.id ?? null;
      if (newRunId !== runIdRef.current) {
        runIdRef.current = newRunId;
        setHeartsSent(0);
      }
    } catch (e: any) {
      console.warn('fetch current-run failed', e.message);
    }
  };

  const fetchScores = async () => {
    try {
      const res = await get<any>(`/competitions/${competitionId}/scores`);
      setScores(res);
    } catch { /* silently ignore */ }
  };

  useEffect(() => {
    setLoading(true);
    fetchCurrent().finally(() => setLoading(false));
    pollRef.current = setInterval(fetchCurrent, 2000);
    fetchScores();
    scoresPollRef.current = setInterval(fetchScores, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (scoresPollRef.current) clearInterval(scoresPollRef.current);
    };
  }, [competitionId]);

  useEffect(() => {
    get<any[]>(`/competitions/${competitionId}/deals`)
      .then(res => res?.length > 0 ? setDeal(res[0]) : null)
      .catch(() => null);
  }, [competitionId]);

  const sendHearts = async (count: number) => {
    if (!data?.current_run || heartsSent >= 3) return;
    const allowed = Math.min(count, 3 - heartsSent);
    if (allowed <= 0) return;
    setSending(true);
    try {
      const sessionId = session.getSessionId(competitionId);
      const res = await post<any>(`/competitions/${competitionId}/hearts`, { run_id: data.current_run.id, count: allowed }, sessionId);
      if (res?.hearts_total != null) {
        setData((d: any) => ({ ...d, current_run: { ...d.current_run, hearts_total: res.hearts_total } }));
      }
      setHeartsSent(h => h + (res?.accepted ?? allowed));
    } catch (e: any) {
      console.warn('send hearts failed', e.message);
    } finally {
      setSending(false);
    }
  };

  const onDealPress = async () => {
    if (!deal) return;
    try {
      const sid = session.getSessionId(competitionId);
      const res = await post<any>(`/deals/${deal.id}/click`, undefined, sid);
      const url = res?.landing_url ?? deal.landing_url;
      if (url) Linking.openURL(url);
    } catch { /* ignore */ }
  };

  if (loading) return <View style={s.center}><ActivityIndicator size="large" color={BLUE} /></View>;

  if (!data || !data.current_run) {
    const noRunDealImg = deal?.image_url ? mediaUrl(deal.image_url) : null;
    return (
      <ScrollView
        style={s.bg}
        contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24, paddingVertical: 40 }}
      >
        <Text style={{ fontSize: 64, color: PINK }}>♥</Text>
        <Text style={{ fontSize: 16, color: TEXT_SUB, textAlign: 'center', lineHeight: 24, marginTop: 12 }}>
          Kom tilbake for å sende hjerter{'\n'}til ditt favorittlag{'\n'}(og gjerne alle andre).
        </Text>
        {noRunDealImg ? (
          <>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => setDealOpen(true)}
              style={{ marginTop: 40, width: '100%', borderRadius: 12, overflow: 'hidden' }}
            >
              <Image source={{ uri: noRunDealImg }} style={{ width: '100%', height: 160 }} resizeMode="cover" />
            </TouchableOpacity>
            <Modal visible={dealOpen} animationType="slide" transparent onRequestClose={() => setDealOpen(false)}>
              <Pressable style={s.modalBackdrop} onPress={() => setDealOpen(false)} />
              <SafeAreaView style={s.modalSheet}>
                <Image source={{ uri: noRunDealImg }} style={s.modalImage} resizeMode="cover" />
                <View style={s.modalBody}>
                  {deal?.sponsor_name ? <Text style={s.modalSponsor}>{deal.sponsor_name.toUpperCase()}</Text> : null}
                  {deal?.headline ? <Text style={s.modalHeadline}>{deal.headline}</Text> : null}
                  {deal?.body_text ? <Text style={s.modalBodyText}>{deal.body_text}</Text> : null}
                  {deal?.promo_code ? (
                    <View style={s.modalCodeRow}>
                      <Text style={s.modalCodeLabel}>Rabattkode</Text>
                      <Text style={s.modalCode}>{deal.promo_code}</Text>
                    </View>
                  ) : null}
                  <TouchableOpacity style={s.modalBtn} onPress={() => { setDealOpen(false); onDealPress(); }}>
                    <Text style={s.modalBtnText}>{deal?.cta_label ?? `Gå til ${deal?.sponsor_name ?? 'tilbudet'}`}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setDealOpen(false)} style={s.modalClose}>
                    <Text style={s.modalCloseText}>Lukk</Text>
                  </TouchableOpacity>
                </View>
              </SafeAreaView>
            </Modal>
          </>
        ) : null}
      </ScrollView>
    );
  }

  const run = data.current_run;
  const team = run.team;
  const routine = run.routine;
  const classLabel = CLASS_LABELS[routine?.class_category] ?? routine?.class_category ?? '';
  const jumpKey = run.jump_type as string | undefined;
  const jumpLabel = jumpKey ? (JUMP_LABELS[jumpKey] ?? jumpKey) : null;
  const jumpDesc = jumpKey ? JUMP_DESC[jumpKey] : null;
  const heartsTotal = run.hearts_total ?? 0;
  const maxReached = heartsSent >= 3;

  // Score to beat: find the class leader in completed scores
  const currentClass = routine?.class_category;
  const classScores = scores?.categories?.find((c: any) => c.category === currentClass);
  const leader = classScores?.teams?.find((t: any) => t.rank === 1);
  const leaderScore: number | null = leader?.score_with_bonus ?? null;

  // Club logo lives on run, not team
  const clubLogoUri = run.club_logo_url ? mediaUrl(run.club_logo_url) : null;

  // Deal image
  const dealImgUri = deal?.image_url ? mediaUrl(deal.image_url) : null;

  return (
    <ScrollView style={s.bg} contentContainerStyle={{ paddingBottom: 32 }}>

      {/* ── Team header ── */}
      <View style={s.heroHeader}>
        <Text style={s.heroSubtitle}>
          {competitionName ? `${competitionName} · ` : ''}på trampetten nå
        </Text>
        <View style={s.heroRow}>
          {clubLogoUri ? (
            <Image source={{ uri: clubLogoUri }} style={s.clubLogo} resizeMode="contain" />
          ) : null}
          <View style={{ flex: 1 }}>
            <Text style={s.heroTeam}>{team.team_name}</Text>
            <Text style={s.heroMeta}>
              {classLabel}
              {team.gymnast_count ? ` · ${team.gymnast_count} utøvere` : ''}
              {routine?.routine_number ? ` · runde ${routine.routine_number}` : ''}
            </Text>
          </View>
        </View>
      </View>

      {/* ── Score to beat ── */}
      {leaderScore != null ? (
        <View style={s.scoreToBeatBox}>
          <Text style={s.scoreToBeatLabel}>Score å slå i {classLabel}</Text>
          <Text style={s.scoreToBeatValue}>{leaderScore.toFixed(3)}</Text>
          <Text style={s.scoreToBeatTeam}>({leader.team_name})</Text>
        </View>
      ) : null}

      {/* ── Jump info box ── */}
      {jumpLabel ? (
        <View style={s.jumpBox}>
          <Text style={s.jumpBoxLabel}>Se etter</Text>
          <Text style={s.jumpBoxTitle}>{jumpLabel}</Text>
          <TouchableOpacity onPress={() => setJumpOpen(true)} style={s.jumpLearnBtn}>
            <Text style={s.jumpLearnText}>Lær mer om hoppet →</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {/* ── Jump description modal ── */}
      <Modal visible={jumpOpen} animationType="fade" transparent onRequestClose={() => setJumpOpen(false)}>
        <View style={s.jumpModalOverlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setJumpOpen(false)} />
          <View style={s.jumpModal}>
            <Text style={s.jumpModalTitle}>{jumpLabel}</Text>
            {jumpDesc ? <Text style={s.jumpModalDesc}>{jumpDesc}</Text> : null}
            <TouchableOpacity onPress={() => setJumpOpen(false)} style={s.modalClose}>
              <Text style={s.modalCloseText}>Lukk</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ── Heart section ── */}
      <View style={s.heartSection}>
        <TouchableOpacity
          onPress={() => sendHearts(1)}
          disabled={sending || maxReached}
          style={[s.heartBtn, (sending || maxReached) && { opacity: 0.45 }]}
          activeOpacity={0.75}
        >
          <Text style={s.heartIcon}>♥</Text>
        </TouchableOpacity>
        <Text style={s.heartPrompt}>Send hjerte til storskjermen</Text>
        <Text style={s.heartsTotal}>{heartsTotal} hjerter sendt for dette laget</Text>
        {maxReached ? <Text style={s.maxText}>Du har sendt maks hjerter for dette laget</Text> : null}
      </View>

      {/* ── Deal banner ── */}
      {deal && dealImgUri ? (
        <TouchableOpacity activeOpacity={0.85} onPress={() => setDealOpen(true)} style={s.dealBanner}>
          <Image source={{ uri: dealImgUri }} style={s.dealBannerImg} resizeMode="cover" />
        </TouchableOpacity>
      ) : null}

      {/* ── Deal modal ── */}
      <Modal visible={dealOpen} animationType="slide" transparent onRequestClose={() => setDealOpen(false)}>
        <Pressable style={s.modalBackdrop} onPress={() => setDealOpen(false)} />
        <SafeAreaView style={s.modalSheet}>
          {dealImgUri ? (
            <Image source={{ uri: dealImgUri }} style={s.modalImage} resizeMode="cover" />
          ) : null}
          <View style={s.modalBody}>
            {deal?.sponsor_name ? (
              <Text style={s.modalSponsor}>{deal.sponsor_name.toUpperCase()}</Text>
            ) : null}
            {deal?.headline ? (
              <Text style={s.modalHeadline}>{deal.headline}</Text>
            ) : null}
            {deal?.body_text ? (
              <Text style={s.modalBodyText}>{deal.body_text}</Text>
            ) : null}
            {deal?.promo_code ? (
              <View style={s.modalCodeRow}>
                <Text style={s.modalCodeLabel}>Rabattkode</Text>
                <Text style={s.modalCode}>{deal.promo_code}</Text>
              </View>
            ) : null}
            <TouchableOpacity style={s.modalBtn} onPress={() => { setDealOpen(false); onDealPress(); }}>
              <Text style={s.modalBtnText}>
                {deal?.cta_label ?? `Gå til ${deal?.sponsor_name ?? 'tilbudet'}`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setDealOpen(false)} style={s.modalClose}>
              <Text style={s.modalCloseText}>Lukk</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  bg:     { flex: 1, backgroundColor: BG },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BG },

  heroHeader:   { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 16 },
  heroSubtitle: { fontSize: 13, color: TEXT_MUTED },
  heroRow:      { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  clubLogo:     { width: 44, height: 44, borderRadius: 6, marginRight: 12 },
  heroTeam:     { fontSize: 26, fontWeight: '700', color: TEXT },
  heroMeta:     { fontSize: 14, color: TEXT_SUB, marginTop: 3 },

  jumpBox: {
    marginHorizontal: 16, marginBottom: 16,
    backgroundColor: BLUE_LIGHT, borderRadius: 10, padding: 14,
    borderLeftWidth: 3, borderLeftColor: BLUE,
  },

  scoreToBeatBox: {
    marginHorizontal: 16, marginBottom: 14,
    backgroundColor: '#FFF8E1', borderRadius: 10, padding: 12,
    borderLeftWidth: 3, borderLeftColor: '#F9A825',
    flexDirection: 'row', alignItems: 'center', gap: 8,
  },
  scoreToBeatLabel: { fontSize: 12, color: '#7A6100', fontWeight: '600', flex: 1 },
  scoreToBeatValue: { fontSize: 18, fontWeight: '800', color: '#7A6100' },
  scoreToBeatTeam:  { fontSize: 11, color: '#A0832A' },

  jumpBoxLabel: { fontSize: 11, color: BLUE, fontWeight: '700', letterSpacing: 0.5, marginBottom: 2 },
  jumpBoxTitle: { fontSize: 17, fontWeight: '700', color: BLUE_DARK },
  jumpLearnBtn: { marginTop: 8, alignSelf: 'flex-start' },
  jumpLearnText: { fontSize: 13, color: BLUE, fontWeight: '600' },

  jumpModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  jumpModal: {
    backgroundColor: '#fff', borderRadius: 16, padding: 22,
    elevation: 8,
    shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 4 },
  },
  jumpModalTitle: { fontSize: 20, fontWeight: '700', color: BLUE_DARK, marginBottom: 10 },
  jumpModalDesc:  { fontSize: 15, color: TEXT_SUB, lineHeight: 23 },

  heartSection: { alignItems: 'center', paddingVertical: 22 },
  heartBtn: {
    width: 130, height: 130, borderRadius: 65,
    backgroundColor: PINK,
    justifyContent: 'center', alignItems: 'center',
    shadowColor: PINK, shadowOpacity: 0.5, shadowRadius: 18, shadowOffset: { width: 0, height: 6 },
    elevation: 10,
  },
  heartIcon:    { fontSize: 52, color: '#fff' },
  heartPrompt:  { marginTop: 14, fontSize: 15, color: TEXT_SUB },
  heartsTotal:  { marginTop: 4, fontSize: 13, color: PINK_DARK, fontWeight: '600' },
  maxText:       { marginTop: 12, fontSize: 13, color: TEXT_MUTED },

  dealBanner: {
    marginTop: 8,
    width: '100%',
    height: 140,
    overflow: 'hidden',
  },
  dealBannerImg: { width: '100%', height: 140 },

  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
  },
  modalSheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 18, borderTopRightRadius: 18,
    overflow: 'hidden',
    maxHeight: '75%',
  },
  modalImage:   { width: '100%', height: 180, resizeMode: 'cover' },
  modalBody:    { padding: 20 },
  modalSponsor: { fontSize: 11, fontWeight: '700', color: '#9E7000', letterSpacing: 0.9 },
  modalHeadline:{ fontSize: 20, fontWeight: '700', color: TEXT, marginTop: 4 },
  modalBodyText:{ fontSize: 14, color: TEXT_SUB, marginTop: 8, lineHeight: 21 },
  modalCodeRow: {
    flexDirection: 'row', alignItems: 'center',
    marginTop: 14, backgroundColor: '#FFF8E1',
    borderRadius: 8, padding: 10, gap: 8,
  },
  modalCodeLabel:{ fontSize: 12, color: '#9E7000', fontWeight: '600' },
  modalCode:     { fontSize: 16, fontWeight: '800', color: TEXT, letterSpacing: 2 },
  modalBtn: {
    marginTop: 18, backgroundColor: '#C9831A',
    borderRadius: 10, paddingVertical: 14, alignItems: 'center',
  },
  modalBtnText:  { color: '#fff', fontWeight: '700', fontSize: 15 },
  modalClose:    { alignItems: 'center', paddingVertical: 14 },
  modalCloseText:{ color: TEXT_MUTED, fontSize: 14 },
});
